import { _IdGenerator } from '@angular/cdk/a11y';
import { ChangeDetectionStrategy, Component, computed, contentChildren, inject, input, output } from '@angular/core';
import { HxBaseControl } from '../../base/hx-base-control';
import type { HxCardAppearance } from '../card';
import { HxCardLink, type HxCardLinkValue } from '../card-link';
import { HxFormGroup } from '../form-group';
import { HxLabel } from '../label/label';
import type { LinkButtonTarget } from '../link-button/link-button';

export interface HxCardLinkGroupItem {
  id?: string | number;
  title: string;
  questionTitle?: string;
  subtitle?: string;
  appearance?: HxCardAppearance;
  linkLabel?: string;
  linkUrl?: string;
  linkTarget?: LinkButtonTarget;
  referralLabel?: string;
  notesLabel?: string;
  notesPlaceholder?: string;
  notesRequired?: boolean;
  referralMade?: boolean;
  referralNotes?: string;
  disabled?: boolean;
}

export type HxCardLinkGroupItemEvent = {
  source: HxCardLinkGroup;
  index: number;
  item: HxCardLinkGroupItem;
};

export type HxCardLinkGroupValueChange = HxCardLinkGroupItemEvent & {
  value: HxCardLinkValue;
};

export type HxCardLinkGroupLinkEvent = HxCardLinkGroupItemEvent & {
  url: string;
};

@Component({
  selector: 'hx-card-link-group',
  standalone: true,
  imports: [HxCardLink, HxFormGroup, HxLabel],
  templateUrl: './card-link-group.html',
  styleUrls: ['./card-link-group-tokens.css', './card-link-group.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.hx-card-link-group]': 'true',
    '[class.hx-card-link-group--grid]': 'layout() === "default"',
    '[class.hx-card-link-group--align-left]': 'align() === "left"',
    '[class.hx-card-link-group--align-center]': 'align() === "center"',
    '[class.hx-card-link-group--align-right]': 'align() === "right"',
    '[class.hx-card-link-group--disabled]': 'disabledState()',
    '[style.--hx-card-link-group-columns]': '_getValidColumns()',
    '[attr.role]': '"group"',
    '[attr.aria-disabled]': 'disabledState() ? "true" : null',
    '[class.hx-card-link-group--readonly]': 'readonly()',
  },
})
export class HxCardLinkGroup extends HxBaseControl {
  private readonly _idGenerator = inject(_IdGenerator);
  protected readonly _labelId = this._idGenerator.getId('hx-card-link-group-label-');

  readonly labelFontWeight = input<string | undefined>('600');
  readonly hint = input<string>('');
  readonly cardsData = input<HxCardLinkGroupItem[]>([]);
  readonly layout = input<'default' | 'wrap'>('default');
  readonly columns = input<number>(3);
  readonly align = input<'left' | 'center' | 'right'>('left');

  readonly cardValueChange = output<HxCardLinkGroupValueChange>();
  readonly linkClicked = output<HxCardLinkGroupLinkEvent>();
  readonly urlCopied = output<HxCardLinkGroupLinkEvent>();

  readonly projectedCards = contentChildren(HxCardLink);
  protected readonly hasProjectedCards = computed(() => this.projectedCards().length > 0);

  protected _getValidColumns(): string | null {
    if (this.layout() !== 'default') return null;
    const cols = this.columns();
    const validCols = Math.max(1, Math.min(5, cols));
    if (cols !== validCols) {
      console.warn(`[HxCardLinkGroup] columns value ${cols} is out of range (1-5). Using ${validCols}.`);
    }
    return validCols.toString();
  }

  protected onReferralMadeChange(value: boolean, card: HxCardLinkGroupItem, index: number): void {
    card.referralMade = value;
    // Clear notes when the toggle is turned OFF so stale text doesn't reappear on re-toggle
    if (!value) {
      card.referralNotes = '';
    }
    this.emitValueChange(card, index);
  }

  protected onReferralNotesChange(value: string, card: HxCardLinkGroupItem, index: number): void {
    card.referralNotes = value;
    this.emitValueChange(card, index);
  }

  protected onLinkClicked(url: string, card: HxCardLinkGroupItem, index: number): void {
    this.linkClicked.emit({ source: this, index, item: card, url });
  }

  protected onUrlCopied(url: string, card: HxCardLinkGroupItem, index: number): void {
    this.urlCopied.emit({ source: this, index, item: card, url });
  }

  protected trackByCard = (index: number, card: HxCardLinkGroupItem): string | number => {
    return card.id ?? card.linkUrl ?? index;
  };

  private emitValueChange(card: HxCardLinkGroupItem, index: number): void {
    const value: HxCardLinkValue = {
      referralMade: card.referralMade ?? false,
      referralNotes: card.referralNotes ?? '',
    };
    this.cardValueChange.emit({ source: this, index, item: card, value });
  }
}
